import React, { useState } from 'react';
import { Session } from '@supabase/supabase-js';
import { BatchAnalysisUploader } from './batch/BatchAnalysisUploader';
import { BatchJobViewer } from './batch/BatchJobViewer';
import { supabase } from '../supabaseClient';

interface BatchProcessingPageProps {
    session: Session;
    onNavigate: (page: string, caseId?: string) => void;
}

export const BatchProcessingPage: React.FC<BatchProcessingPageProps> = ({ session, onNavigate }) => {
    const [selectedBatchJob, setSelectedBatchJob] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const handleBatchCreated = (batchJobId: string) => {
        setSelectedBatchJob(batchJobId);
        setRefreshKey(prev => prev + 1); // Force refresh
    };

    const handleBackToList = () => {
        setSelectedBatchJob(null);
        setRefreshKey(prev => prev + 1); // Force refresh
    };

    return (
        <div className="flex flex-col flex-1 p-4 overflow-y-auto bg-luna-bg-light dark:bg-luna-bg-dark">
            <div className="flex flex-col gap-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-luna-text-light dark:text-luna-text-dark">Batch Document Processing</h1>
                        <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                            Process multiple documents simultaneously with AI-powered analysis
                        </p>
                    </div>
                    {selectedBatchJob && (
                        <button
                            onClick={handleBackToList}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-luna-text-light dark:text-luna-text-dark bg-luna-bg-light dark:bg-luna-bg-dark border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg hover:bg-luna-text-light/10 dark:hover:bg-luna-text-dark/10 transition-colors"
                        >
                            <span className="material-symbols-outlined">arrow_back</span>
                            Back to Upload
                        </button>
                    )}
                </div>

                {/* Main Content */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Batch Uploader Panel */}
                    <div className="lg:col-span-1">
                        <div className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm p-6">
                            <h2 className="text-lg font-bold text-luna-text-light dark:text-luna-text-dark mb-4">
                                {selectedBatchJob ? 'Batch Details' : 'Upload Documents'}
                            </h2>

                            {!selectedBatchJob ? (
                                <BatchAnalysisUploader
                                    session={session}
                                    onBatchCreated={handleBatchCreated}
                                />
                            ) : (
                                <BatchJobViewer
                                    key={refreshKey}
                                    session={session}
                                    batchJobId={selectedBatchJob}
                                />
                            )}
                        </div>
                    </div>

                    {/* Information Panel */}
                    <div className="lg:col-span-2">
                        <div className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm p-6">
                            <h2 className="text-lg font-bold text-luna-text-light dark:text-luna-text-dark mb-4">How Batch Processing Works</h2>

                            <div className="space-y-4">
                                <div className="flex items-start gap-3">
                                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-luna-secondary/10 text-luna-secondary">
                                        <span className="material-symbols-outlined text-sm">upload</span>
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-luna-text-light dark:text-luna-text-dark">Upload Multiple Documents</h3>
                                        <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                            Drag and drop or select multiple legal documents for simultaneous processing.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-luna-secondary/10 text-luna-secondary">
                                        <span className="material-symbols-outlined text-sm">auto_awesome</span>
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-luna-text-light dark:text-luna-text-dark">AI-Powered Analysis</h3>
                                        <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                            Our AI analyzes each document for compliance risks, key clauses, and legal issues.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-luna-secondary/10 text-luna-secondary">
                                        <span className="material-symbols-outlined text-sm">compare</span>
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-luna-text-light dark:text-luna-text-dark">Comparative Insights</h3>
                                        <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                            Get cross-document analysis highlighting similarities, differences, and patterns.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-luna-secondary/10 text-luna-secondary">
                                        <span className="material-symbols-outlined text-sm">download</span>
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-luna-text-light dark:text-luna-text-dark">Export Results</h3>
                                        <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                            Download detailed reports in PDF, DOCX, or CSV formats for your records.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 p-4 bg-luna-secondary/5 dark:bg-luna-secondary/10 rounded-lg border border-luna-secondary/20">
                                <h4 className="font-medium text-luna-text-light dark:text-luna-text-dark flex items-center gap-2">
                                    <span className="material-symbols-outlined text-luna-secondary">lightbulb</span>
                                    Pro Tip
                                </h4>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 mt-1">
                                    Batch processing can handle up to 100 documents simultaneously. For best results,
                                    ensure documents are in PDF, DOCX, or TXT formats.
                                </p>
                            </div>
                        </div>

                        {/* Recent Batch Jobs */}
                        <div className="mt-6 bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm p-6">
                            <h2 className="text-lg font-bold text-luna-text-light dark:text-luna-text-dark mb-4">Recent Batch Jobs</h2>
                            <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                Your recent batch processing jobs will appear here for quick access.
                            </p>
                            {/* In a real implementation, this would show a list of recent batch jobs */}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BatchProcessingPage;